import React from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { ConfigProvider } from 'antd';
import zhCN from 'antd/locale/zh_CN';
import 'antd/dist/reset.css';
import { useVersionCheck } from './hooks/useVersionCheck';
import { MENU_PATH_CODE_MAP } from './constants/menuResource';
import { hasPerm } from './hooks/usePermission';
import Login from './pages/Login/Login';
import MainLayout from './components/MainLayout/MainLayout';
import Home from './pages/Home/Home';
import SerialList from './pages/Serial/SerialList';
import SerialImport from './pages/Serial/SerialImport';
import OldSerialImport from './pages/Serial/OldSerialImport';
import ImportRecords from './pages/Serial/ImportRecords';
import VoidRecords from './pages/Serial/VoidRecords';
import UnbindRecords from './pages/Serial/UnbindRecords';
import ResetRecords from './pages/Serial/ResetRecords';
import Shipment from './pages/Serial/Shipment';
import Return from './pages/Serial/Return';
import OrderList from './pages/Order/OrderList';
import AuthAlerts from './pages/Auth/AuthAlerts';
import PlatformConfig from './pages/Config/PlatformConfig';
import MacRuleConfig from './pages/Config/MacRuleConfig';
import FactoryManage from './pages/Config/FactoryManage';
import NotifyConfig from './pages/Config/NotifyConfig';
import AccountManage from './pages/System/AccountManage';
import RoleManage from './pages/System/RoleManage';
import './App.css';

// 简单的鉴权组件：校验 localStorage 中是否存在 token，并按菜单 code 校验访问权限
const PrivateRoute = ({ children }) => {
  const location = useLocation();
  const token = localStorage.getItem('user_token');
  if (!token) return <Navigate to="/login" />;
  const code = MENU_PATH_CODE_MAP[location.pathname];
  if (code && !hasPerm(code)) return <Navigate to="/" replace />;
  return children;
};

// 通用占位页：新增路由时可先挂此组件占位
const Placeholder = ({ title }) => (
  <div style={{ padding: '24px', background: '#fff', margin: '24px', borderRadius: '8px', minHeight: 'calc(100vh - 120px)' }}>
    <h2 style={{ fontSize: '20px', fontWeight: '500', marginBottom: '8px' }}>{title}</h2>
    <p style={{ fontSize: '14px', color: '#999' }}>页面开发中......</p>
  </div>
);

function App() {
  // 版本检查：每 30 秒轮询 /version.json，检测到更新自动刷新
  useVersionCheck(30000);

  return (
    <ConfigProvider locale={zhCN}>
      <BrowserRouter>
        <Routes>
          <Route path="/login" element={<Login />} />

          {/* 所有需鉴权的路由包裹在 MainLayout 中 */}
          <Route
            path="/*"
            element={
              <PrivateRoute>
                <MainLayout>
                  <Routes>
                    <Route path="/" element={<Home />} />
                    <Route path="/home/dashboard" element={<Placeholder title="工作台" />} />

                    {/* 串码管理 */}
                    <Route path="/serial/list" element={<SerialList />} />
                    <Route path="/serial/import" element={<SerialImport />} />
                    <Route path="/serial/import-old" element={<OldSerialImport />} />
                    <Route path="/serial/import-records" element={<ImportRecords />} />
                    <Route path="/serial/void-records" element={<VoidRecords />} />
                    <Route path="/serial/unbind-records" element={<UnbindRecords />} />
                    <Route path="/serial/reset-records" element={<ResetRecords />} />
                    <Route path="/serial/shipment" element={<Shipment />} />
                    <Route path="/serial/return" element={<Return />} />

                    {/* 生产工单 */}
                    <Route path="/order/list" element={<OrderList />} />

                    {/* 授权管理 */}
                    <Route path="/auth/alerts" element={<AuthAlerts />} />

                    {/* 基础配置 */}
                    <Route path="/config/platforms" element={<PlatformConfig />} />
                    <Route path="/config/mac-rule" element={<MacRuleConfig />} />
                    <Route path="/config/factories" element={<FactoryManage />} />
                    <Route path="/config/notify" element={<NotifyConfig />} />

                    {/* 系统管理 */}
                    <Route path="/system/accounts" element={<AccountManage />} />
                    <Route path="/system/roles" element={<RoleManage />} />
                  </Routes>
                </MainLayout>
              </PrivateRoute>
            }
          />
        </Routes>
      </BrowserRouter>
    </ConfigProvider>
  );
}

export default App;
